import { Jsona, SwitchCaseJsonMapper } from "jsona";
import { snakeCase } from "lodash-es";

const dataFormatter = new Jsona({
  jsonPropertiesMapper: new SwitchCaseJsonMapper({
    camelizeAttributes: true,
    camelizeRelationships: true,
    camelizeType: true,
    camelizeMeta: true,
    switchChar: "-",
  }),
});

const convertKeysToSnakeCase = (obj: unknown): unknown => {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(convertKeysToSnakeCase);
  if (typeof obj === "object") {
    const converted: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      converted[snakeCase(key)] = convertKeysToSnakeCase(value);
    }
    return converted;
  }
  return obj;
};

type ApiPayload = {
  data: {
    attributes: Record<string, unknown>;
  };
};

export const buildApiPayload = (
  attributes: Record<string, unknown>
): ApiPayload => {
  const snakeCaseAttributes = convertKeysToSnakeCase(attributes);

  return {
    data: {
      attributes: snakeCaseAttributes as Record<string, unknown>,
    },
  };
};

/**
 * Parses a JSON API response and deserializes it using jsona
 * Converts the JSON API format into regular JavaScript objects
 *
 * @param json - The JSON API response object
 * @returns The deserialized object with camelCase properties
 */
export const parseApiResponse = <T>(json: unknown): T => {
  return dataFormatter.deserialize(json as Record<string, unknown>) as T;
};

type ApiErrorResponse = {
  errors: string[];
};

export class ApiError extends Error {
  errors: string[];

  constructor(errors: string[]) {
    super(errors.join(", "));
    this.errors = errors;
    this.name = "ApiError";
  }
}

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const data: ApiErrorResponse = await response.json();
    throw new ApiError(data.errors || ["Request failed"]);
  }
  const json = await response.json();
  return parseApiResponse<T>(json);
};

type FetchOptions = Omit<RequestInit, "body" | "method"> & {
  body?: Record<string, unknown> | string;
};

const defaultHeaders = {
  "Content-Type": "application/json",
  Accept: "application/json",
};

const createFetchOptions = async (
  method: string,
  options?: FetchOptions
): Promise<RequestInit> => {
  const { body, headers, ...restOptions } = options || {};

  const { token } = await fetch("/csrf", { credentials: "include" }).then((r) =>
    r.json()
  );

  return {
    method,
    headers: {
      ...defaultHeaders,
      ...headers,
      "X-CSRF-Token": token,
    },
    credentials: "include",
    ...(body && {
      body:
        typeof body === "string" ? body : JSON.stringify(buildApiPayload(body)),
    }),
    ...restOptions,
  };
};

/**
 * Performs a GET request to the API
 *
 * @param url - The URL to fetch
 * @param options - Optional fetch options
 * @returns The parsed and deserialized response
 */
export const apiGet = async <T>(
  url: string,
  options?: FetchOptions
): Promise<T> => {
  const response = await fetch(url, await createFetchOptions("GET", options));
  return handleResponse<T>(response);
};

/**
 * Performs a POST request to the API
 *
 * @param url - The URL to fetch
 * @param body - The request body (will be converted to JSON API format)
 * @param options - Optional fetch options
 * @returns The parsed and deserialized response
 */
export const apiPost = async <T>(
  url: string,
  body?: Record<string, unknown>,
  options?: FetchOptions
): Promise<T> => {
  const response = await fetch(
    url,
    await createFetchOptions("POST", { ...options, body })
  );
  return handleResponse<T>(response);
};

/**
 * Performs a PUT request to the API
 *
 * @param url - The URL to fetch
 * @param body - The request body (will be converted to JSON API format)
 * @param options - Optional fetch options
 * @returns The parsed and deserialized response
 */
export const apiPut = async <T>(
  url: string,
  body?: Record<string, unknown>,
  options?: FetchOptions
): Promise<T> => {
  const response = await fetch(
    url,
    await createFetchOptions("PUT", { ...options, body })
  );
  return handleResponse<T>(response);
};

/**
 * Performs a PATCH request to the API
 *
 * @param url - The URL to fetch
 * @param body - The request body (will be converted to JSON API format)
 * @param options - Optional fetch options
 * @returns The parsed and deserialized response
 */
export const apiPatch = async <T>(
  url: string,
  body?: Record<string, unknown>,
  options?: FetchOptions
): Promise<T> => {
  const response = await fetch(
    url,
    await createFetchOptions("PATCH", { ...options, body })
  );
  return handleResponse<T>(response);
};

/**
 * Performs a DELETE request to the API
 *
 * @param url - The URL to fetch
 * @param options - Optional fetch options
 * @returns void if successful, throws error if not
 */
export const apiDelete = async (
  url: string,
  options?: FetchOptions
): Promise<void> => {
  const response = await fetch(
    url,
    await createFetchOptions("DELETE", options)
  );

  if (response.status === 401) {
    return;
  }

  if (!response.ok) {
    let errors: string[] = ["Delete request failed"];

    try {
      const data = (await response.json()) as { errors?: string[] };
      if (data.errors && data.errors.length > 0) {
        errors = data.errors;
      } else {
        errors = [`Delete request failed (${response.status})`];
      }
    } catch {
      errors = [`Delete request failed (${response.status})`];
    }

    throw new ApiError(errors);
  }
};
